// src/services/visitors.service.js
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from './firebase/firebaseconfig';

const visitorsService = {
  // Get visitor statistics
  getVisitorStats: async () => {
    try {
      const statsRef = doc(db, 'siteStats', 'visitors');
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        const data = statsDoc.data();
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        const monthlyKey = `visits_${currentYear}_${currentMonth}`;

        // Generate monthly data for last 12 months
        const monthlyData = [];
        for (let i = 11; i >= 0; i--) {
          const date = new Date(currentYear, currentMonth - 1 - i, 1);
          const key = `visits_${date.getFullYear()}_${date.getMonth() + 1}`;
          monthlyData.push({
            month: date.toLocaleDateString('fr-FR', { month: 'short' }),
            year: date.getFullYear(),
            visitors: data[key] || 0
          });
        }

        return {
          success: true,
          stats: {
            totalVisitors: data.totalVisitors || 0,
            monthlyVisitors: data[monthlyKey] || 0,
            lastUpdated: data.lastUpdated?.toDate() || new Date(),
            monthlyData
          }
        };
      } else {
        return {
          success: true,
          stats: {
            totalVisitors: 0,
            monthlyVisitors: 0,
            lastUpdated: new Date(),
            monthlyData: []
          }
        };
      }
    } catch (error) {
      console.error('Error getting visitor stats:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Track a new visitor
  trackVisitor: async () => {
    try {
      const statsRef = doc(db, 'siteStats', 'visitors');
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;
      const monthlyKey = `visits_${currentYear}_${currentMonth}`;

      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        await updateDoc(statsRef, {
          totalVisitors: increment(1),
          lastUpdated: new Date(),
          [monthlyKey]: increment(1)
        });
      } else {
        await setDoc(statsRef, {
          totalVisitors: 1,
          lastUpdated: new Date(),
          [monthlyKey]: 1
        });
      }

      return {
        success: true,
        message: 'Visitor tracked successfully'
      };
    } catch (error) {
      console.error('Error tracking visitor:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get visitor data for a specific month
  getMonthlyVisitors: async (year, month) => {
    try {
      const statsRef = doc(db, 'siteStats', 'visitors');
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        const data = statsDoc.data();
        const monthlyKey = `visits_${year}_${month}`;
        
        return {
          success: true,
          visitors: data[monthlyKey] || 0
        };
      } else {
        return {
          success: true,
          visitors: 0
        };
      }
    } catch (error) {
      console.error('Error getting monthly visitors:', error);
      return {
        success: false,
        error: error.message
      };
    }
  },

  // Get visitor trends (comparison with previous period)
  getVisitorTrends: async () => {
    try {
      const statsRef = doc(db, 'siteStats', 'visitors');
      const statsDoc = await getDoc(statsRef);

      if (statsDoc.exists()) {
        const data = statsDoc.data();
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth() + 1;
        
        const currentMonthKey = `visits_${currentYear}_${currentMonth}`;
        const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;
        const previousYear = currentMonth === 1 ? currentYear - 1 : currentYear;
        const previousMonthKey = `visits_${previousYear}_${previousMonth}`;

        const currentMonthVisitors = data[currentMonthKey] || 0;
        const previousMonthVisitors = data[previousMonthKey] || 0;

        const change = previousMonthVisitors > 0 
          ? ((currentMonthVisitors - previousMonthVisitors) / previousMonthVisitors) * 100 
          : 0;

        return {
          success: true,
          trends: {
            currentMonth: currentMonthVisitors,
            previousMonth: previousMonthVisitors,
            change: Math.round(change * 10) / 10,
            isPositive: change >= 0
          }
        };
      } else {
        return {
          success: true,
          trends: {
            currentMonth: 0,
            previousMonth: 0,
            change: 0,
            isPositive: true
          }
        };
      }
    } catch (error) {
      console.error('Error getting visitor trends:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }
};

export default visitorsService;